import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BookOutlined,
  CloseOutlined,
  EyeOutlined,
  RightOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useDashboardStore from "features/dashboard/store/DashboardStore";
import { GetRecentlyAddedBooksSummary } from "features/dashboard/api/hooks";
import { IRecentlyAddedBook } from "features/dashboard/api/types";
import { formatDateTimeIST } from "utils/date";

const RecentlyAddedBooksWidget = () => {
  const navigate = useNavigate();
  const { setValue } = useDashboardStore();
  const { data: summaryResponse, isLoading } = GetRecentlyAddedBooksSummary();

  const [selectedBook, setSelectedBook] = useState<IRecentlyAddedBook | null>(
    null,
  );

  const summary = summaryResponse?.apiData;
  const recentBooks = summary?.recentBooks || [];

  const handleViewAll = () => {
    setValue("10");
    navigate("/admin/dashboard/statistics-dashboard");
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          my: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <CardHeader
          title={
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <BookOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  Recently Added Books
                </Typography>
              </Stack>
              {summary && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`Total Books: ${summary.totalBooks ?? 0}`}
                    color="primary"
                    variant="filled"
                    sx={{ fontWeight: "bold" }}
                  />
                  <Chip
                    size="small"
                    label={`Last 7 Days: +${summary.addedLast7Days ?? 0}`}
                    color="success"
                    variant="outlined"
                    sx={{ fontWeight: "bold" }}
                  />
                  <Chip
                    size="small"
                    label={`Last 30 Days: +${summary.addedLast30Days ?? 0}`}
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: "bold" }}
                  />
                </Stack>
              )}
            </Stack>
          }
          action={
            <Button
              variant="outlined"
              size="small"
              endIcon={<RightOutlined />}
              onClick={handleViewAll}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              View All & Export
            </Button>
          }
          sx={{ pb: 1.5 }}
        />
        <Divider />
        <CardContent sx={{ pt: 2 }}>
          {isLoading ? (
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton
                    variant="rectangular"
                    height={160}
                    sx={{ borderRadius: 1.5 }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : recentBooks.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
              <Typography variant="body1">
                No recently added books found.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {recentBooks.map((book) => {
                const authorDisplay =
                  book.authorNameCSV || book.primaryAuthorName || "Unknown";
                const accCount = book.accessionCount ?? 0;

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={book.materialID}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "grey.200",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "primary.main",
                          boxShadow: 3,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                          mb={1}
                        >
                          <Tooltip title={book.title} placement="top">
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: 1.3,
                                minHeight: "2.6em",
                              }}
                            >
                              {book.title}
                            </Typography>
                          </Tooltip>
                          <IconButton
                            size="medium"
                            color="primary"
                            onClick={() => setSelectedBook(book)}
                            title="View Details"
                          >
                            <EyeOutlined />
                          </IconButton>
                        </Stack>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mb: 1,
                          }}
                        >
                          <strong>Author:</strong> {authorDisplay}
                        </Typography>

                        {book.isbn && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>ISBN:</strong> {book.isbn}
                          </Typography>
                        )}

                        {book.classificationCode && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Classification Code:</strong>{" "}
                            {book.classificationCode}
                          </Typography>
                        )}

                        {book.publicationName && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              mb: 0.5,
                            }}
                          >
                            <strong>Publisher:</strong> {book.publicationName}
                            {book.publicationYear
                              ? ` (${book.publicationYear})`
                              : ""}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed #e0e0e0" }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Tooltip
                            title={
                              book.accessionNoCSV
                                ? `Accessions: ${book.accessionNoCSV}`
                                : "No accession numbers"
                            }
                            placement="top"
                          >
                            <Chip
                              size="small"
                              icon={<TagOutlined />}
                              label={`${accCount} ${
                                accCount.toString() === "1" ? "Copy" : "Copies"
                              }`}
                              color={accCount.toString() > "0" ? "primary" : "default"}
                              variant="light"
                              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                            />
                          </Tooltip>

                          {book.created && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              {formatDateTimeIST(book.created).split(" ")[0]}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Book Detail Modal */}
      <Dialog
        open={Boolean(selectedBook)}
        onClose={() => setSelectedBook(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedBook && (
          <>
            <DialogTitle sx={{ m: 0, p: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Book Details
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={() => setSelectedBook(null)}
                  size="small"
                >
                  <CloseOutlined />
                </IconButton>
              </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3 }}>
              <Typography
                variant="h3"
                color="primary"
                sx={{ fontWeight: 700, mb: 2 }}
              >
                {selectedBook.title}
              </Typography>

              <Grid container spacing={2.5}>
                

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Author(s)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedBook.authorNameCSV ||
                      selectedBook.primaryAuthorName ||
                      "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Language
                  </Typography>
                  <Typography variant="body1">
                    {selectedBook.languageName || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Classification Code
                  </Typography>
                  <Typography variant="body1">
                    {selectedBook.classificationCode || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Edition
                  </Typography>
                  <Typography variant="body1">
                    {selectedBook.edition || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Publisher & Year
                  </Typography>
                  <Typography variant="body1">
                    {selectedBook.publicationName || "-"}{" "}
                    {selectedBook.publicationYear
                      ? `(${selectedBook.publicationYear})`
                      : ""}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pages / Physical Details
                  </Typography>
                  <Typography variant="body1">
                    {[
                      selectedBook.pages ? `${selectedBook.pages}p` : null,
                      selectedBook.size ? `Size: ${selectedBook.size}` : null,
                    ]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subject / Classification
                  </Typography>
                  <Typography variant="body1">
                    {selectedBook.classificationName || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Material Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedBook.materialTypeName || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date Added
                  </Typography>
                  <Typography variant="body1">
                    {selectedBook.created
                      ? formatDateTimeIST(selectedBook.created)
                      : "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    ISBN
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedBook.isbn || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Accession Numbers ({selectedBook.accessionCount ?? 0}{" "}
                    Copies)
                  </Typography>
                  {selectedBook.accessionNoCSV ? (
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: "grey.100",
                        borderRadius: 1,
                        maxHeight: 150,
                        overflowY: "auto",
                      }}
                    >
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {selectedBook.accessionNoCSV
                          .split(",")
                          .map((acc, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={acc.trim()}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No accession numbers allocated.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedBook(null);
                  handleViewAll();
                }}
              >
                View in Full Table
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setSelectedBook(null)}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default RecentlyAddedBooksWidget;
